/**
 * Date and time inputs, filled and read in local time.
 *
 * An `<input type="date">` holds a calendar day and an `<input type="time">`
 * a clock time, both in the zone of whoever fills them, and the forms read
 * them back that way — `new Date('2027-03-01T23:30')` is local. Filling a date
 * input from `toISOString().slice(0, 10)` put the UTC day in it instead: at
 * UTC-3 an evening session's form opened on the next day beside the right
 * time, and saving it unchanged moved the session a day; a campaign's end,
 * saved at the end of its local day, read back as the day after, and every
 * save moved it another. East of UTC a day's start read back as the day before.
 *
 * Shown dates go through format-date.ts; these are the values an input holds.
 */

/** Epoch seconds — the API's `*_utc` columns — or a Date. */
type Instant = number | Date;

const toDate = (instant: Instant) => (typeof instant === 'number' ? new Date(instant * 1000) : instant);
const pad = (n: number) => String(n).padStart(2, '0');

/** The local day of an instant, as a date input holds it: 2027-03-01. */
export function localDateInput(instant: Instant): string {
  const d = toDate(instant);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** The local clock time of an instant, as a time input holds it: 23:30. */
export function localTimeInput(instant: Instant): string {
  const d = toDate(instant);
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/**
 * The instant a date input and a time input name together, read in local
 * time as they are written, in epoch seconds — what a session's form saves.
 */
export function instantFromInputs(date: string, time: string): number {
  return Math.floor(new Date(`${date}T${time}`).getTime() / 1000);
}
